import { Sun, Moon, Monitor } from 'lucide-react'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/utils/cn'
import Tooltip from './Tooltip'

export interface ThemeToggleProps {
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const ThemeToggle = ({
  showLabel = false,
  size = 'md',
  className,
}: ThemeToggleProps) => {
  const { theme, toggleTheme } = useUIStore()

  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  }

  const buttonSizes = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
  }

  const getIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className={sizes[size]} />
      case 'dark':
        return <Moon className={sizes[size]} />
      default:
        return <Monitor className={sizes[size]} />
    }
  }

  const getLabel = () => {
    switch (theme) {
      case 'light':
        return 'Light mode'
      case 'dark':
        return 'Dark mode'
      default:
        return 'System'
    }
  }

  const button = (
    <button
      onClick={toggleTheme}
      className={cn(
        'rounded-lg transition-colors',
        'text-slate-600 dark:text-slate-400',
        'hover:bg-slate-100 dark:hover:bg-slate-700',
        'hover:text-slate-900 dark:hover:text-slate-100',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-light-icy-blue/50',
        buttonSizes[size],
        showLabel && 'flex items-center gap-2',
        className
      )}
      aria-label={`Current theme: ${getLabel()}. Click to toggle.`}
    >
      {getIcon()}
      {showLabel && (
        <span className="text-sm font-medium">{getLabel()}</span>
      )}
    </button>
  )

  if (showLabel) {
    return button
  }

  return (
    <Tooltip content={`Theme: ${getLabel()}`} position="bottom">
      {button}
    </Tooltip>
  )
}

export default ThemeToggle
